import React from "react";
import Drawer from "@mui/material/Drawer";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import LoyaltyRoundedIcon from "@mui/icons-material/LoyaltyRounded";
import { toast } from "react-toastify";

import {
  API_get_company_users,
  API_send_welcome_to_company_user,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { buildCompanyOptionsFromStoreNames } from "../../../utilities/companies";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import CompanyNameSidebar from "../home/top_bar_components/CompanyNameSidebar";
import { formatName, formatPhoneNumber, isValidSMSPhone } from "../../../helpers/helpers";
import {
  SPECIAL_ACCESS_LABELS,
  SPECIAL_ACCESS_ICONS,
  SPECIAL_ACCESS_STYLES,
  isAutoGeneratedEmail,
} from "../requests/manager/employees/EmployeesTableRow";
import BulkGrantStoreAccessModal from "./company_team_modals/BulkGrantStoreAccessModal";
import BulkArchiveModal from "./company_team_modals/BulkArchiveModal";
import BulkChangePermissionModal from "./company_team_modals/BulkChangePermissionModal";
import BulkSpecialAccessModal from "./company_team_modals/BulkSpecialAccessModal";
import UserProfileSection from "./company_team_modals/UserProfileSection";
import MembershipActionMenu from "./company_team_modals/MembershipActionMenu";
import AddUserModal from "./company_team_modals/AddUserModal";
import { STORE_OVERRIDE_OPTIONS } from "./company_team_modals/constants";


const PERMISSION_TYPE_OPTIONS = [
  { value: "company_admin", label: "Admin" },
  { value: "company_manager", label: "Manager" },
  { value: "company_employee", label: "Employee" },
  { value: "company_timeclock", label: "Time Clock" },
];
const PERMISSION_TYPE_LABEL = Object.fromEntries(
  PERMISSION_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

const PERMISSION_BADGE_STYLE = {
  rightwork_internal: "border border-violet-600 bg-violet-50 text-violet-600",
  company_admin: "border border-violet-600 bg-violet-50 text-violet-600",
  company_manager: "border border-orange-600 bg-orange-50 text-orange-600",
  company_employee: "border border-blue-600 bg-blue-50 text-blue-600",
  company_timeclock: "border border-yellow-600 bg-yellow-50 text-yellow-700",
};

const VIRTUAL_OVERRIDE_LABELS = {
  billing_access: "Billing",
  onboarding_access: "Onboarding",
};

// Combined for the override filter dropdown.
const OVERRIDE_FILTER_OPTIONS = [
  ...Object.entries(SPECIAL_ACCESS_LABELS).map(([value, label]) => ({ value, label })),
  { value: "billing_access", label: "Billing", isVirtual: true },
  { value: "onboarding_access", label: "Onboarding", isVirtual: true },
];

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived only" },
  { value: "all", label: "All" },
];

// Shared MUI styling, mirroring ActionBarDisplay2 so toolbar buttons / menus
// look the same across the app.
const filterButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "white",
  px: 1.75,
  py: 1,
  fontSize: 14,
  fontWeight: 500,
  minWidth: 0,
  lineHeight: 1.4,
  height: "40px",
  "& .MuiButton-startIcon": { marginRight: "8px" },
  "& .MuiButton-endIcon": { marginLeft: "4px" },
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
};

const filterMenuPaperProps = {
  elevation: 0,
  sx: {
    mt: 1,
    minWidth: 240,
    maxHeight: 520,
    borderRadius: 2,
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
    overflow: "auto",
    "& .MuiList-root": { py: 0.5 },
  },
};


function formatLastLogin(isoString) {
  if (!isoString) return "Never";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}


// Only shown for never-logged-in users. last_welcome_sent_at can be null even
// when sends happened (sends predating the timestamp field), so don't render
// "sent never" for that combination.
function formatInviteStatus(user) {
  if (!user.welcome_send_count) return "No invite sent";
  if (!user.last_welcome_sent_at) return "Invite sent";
  return `Invite sent ${formatLastLogin(user.last_welcome_sent_at).toLowerCase()}`;
}


function PermissionBadge({ permissionType, tooltipTitle, count, total }) {
  if (!permissionType) return null;
  const style = PERMISSION_BADGE_STYLE[permissionType] || PERMISSION_BADGE_STYLE.company_employee;
  const showFraction = count != null && total != null && total > 0 && count < total;
  const badge = (
    <div className={`${style} px-2 h-6 rounded-md flex items-center justify-center gap-1.5 text-xs w-fit`}>
      <p>{PERMISSION_TYPE_LABEL[permissionType] || permissionType}</p>
      {showFraction && (
        <span className="text-[10px] font-semibold opacity-75">
          {count}/{total}
        </span>
      )}
    </div>
  );
  if (!tooltipTitle) return badge;
  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      {badge}
    </Tooltip>
  );
}


function StoreOverrideIcon({ accessKey, tooltipTitle, count, total }) {
  const Icon = SPECIAL_ACCESS_ICONS[accessKey];
  const styles = SPECIAL_ACCESS_STYLES[accessKey];
  const label = SPECIAL_ACCESS_LABELS[accessKey];
  if (!Icon || !styles || !label) return null;
  const showFraction = count != null && total != null && total > 0 && count < total;
  return (
    <Tooltip title={tooltipTitle || label} placement="top" arrow>
      <div
        className={`h-6 rounded border ${styles.border} ${styles.bg} flex items-center justify-center ${
          showFraction ? "px-1.5 gap-1" : "w-6"
        }`}
      >
        <Icon className={styles.text} style={{ fontSize: "13px" }} />
        {showFraction && (
          <span className={`${styles.text} text-[10px] font-semibold leading-none`}>
            {count}/{total}
          </span>
        )}
      </div>
    </Tooltip>
  );
}


function VirtualOverrideBadge({ accessKey }) {
  const label = VIRTUAL_OVERRIDE_LABELS[accessKey];
  if (!label) return null;
  return (
    <Tooltip title={`${label} (company-level)`} placement="top" arrow>
      <div className="px-2 h-6 rounded border border-emerald-400 bg-emerald-50 flex items-center justify-center text-xs text-emerald-700">
        <LoyaltyRoundedIcon style={{ fontSize: "12px", marginRight: 3 }} />
        {label}
      </div>
    </Tooltip>
  );
}


export function MultiSelectFilter({ label, value, options, onChange, icon: Icon }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const hasSelection = value.length > 0;

  const toggle = (optValue) => {
    onChange(
      value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue],
    );
  };

  return (
    <>
      <Button
        disableRipple
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={
          Icon && (
            <Icon sx={{ fontSize: 16, color: hasSelection ? "#6d28d9" : "#1e293b" }} />
          )
        }
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#64748b" }} />}
        sx={{
          ...filterButtonSx,
          color: hasSelection ? "#6d28d9" : "#1e293b",
          borderColor: hasSelection ? "#c4b5fd" : "#e2e8f0",
        }}
      >
        {label}
        {hasSelection && (
          <span
            style={{
              marginLeft: 8,
              backgroundColor: "#ede9fe",
              color: "#6d28d9",
              borderRadius: 999,
              minWidth: 18,
              height: 18,
              padding: "0 6px",
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {value.length}
          </span>
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={filterMenuPaperProps}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {options.length === 0 && (
          <MenuItem disabled sx={{ py: 1 }}>
            <ListItemText
              primaryTypographyProps={{ fontSize: 13, color: "#94a3b8" }}
            >
              No options available
            </ListItemText>
          </MenuItem>
        )}
        {options.map((opt) => {
          const checked = value.includes(opt.value);
          return (
            <MenuItem
              key={opt.value}
              onClick={() => toggle(opt.value)}
              sx={{ py: 0.5, pl: 1, pr: 2 }}
            >
              <Checkbox
                checked={checked}
                disableRipple
                size="small"
                sx={{
                  color: "#cbd5e1",
                  "&.Mui-checked": { color: "#6d28d9" },
                  py: 0.5,
                  pr: 1,
                }}
              />
              <ListItemText
                primaryTypographyProps={{
                  fontSize: 14,
                  color: checked ? "#6d28d9" : "#334155",
                  fontWeight: checked ? 600 : 400,
                }}
              >
                {opt.label}
              </ListItemText>
              {opt.isVirtual && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 10,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#059669",
                  }}
                >
                  company
                </span>
              )}
              {opt.badge && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: "2px 6px",
                    borderRadius: 4,
                    fontSize: 10,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    backgroundColor: "#f1f5f9",
                  }}
                >
                  {opt.badge}
                </span>
              )}
            </MenuItem>
          );
        })}
        {hasSelection && <Divider sx={{ my: 0.5 }} />}
        {hasSelection && (
          <MenuItem
            onClick={() => {
              onChange([]);
              setAnchorEl(null);
            }}
            sx={{ pl: 2.5, py: 0.75 }}
          >
            <ListItemText
              primaryTypographyProps={{ fontSize: 13, color: "#64748b" }}
            >
              Clear selection
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}


function StatusFilter({ value, onChange }) {
  return (
    <div
      role="group"
      aria-label="Filter by archived status"
      className="inline-flex h-[40px] rounded-lg border border-slate-200 bg-white p-1"
    >
      {STATUS_FILTER_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={`rounded-md px-3 text-xs font-medium transition-colors ${
            value === opt.value
              ? "bg-violet-700 text-white"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}


function SortIcon({ active, direction }) {
  if (!active) {
    return <UnfoldMoreIcon style={{ fontSize: "14px" }} className="text-slate-300" />;
  }
  return direction === "asc" ? (
    <ArrowUpwardIcon style={{ fontSize: "14px" }} className="text-violet-700" />
  ) : (
    <ArrowDownwardIcon style={{ fontSize: "14px" }} className="text-violet-700" />
  );
}


function SortableTh({ label, sortKey, currentSort, currentDir, onChange, widthClass, alignRight }) {
  const isActive = currentSort === sortKey;
  return (
    <th
      scope="col"
      className={`${widthClass} px-3 py-3.5 text-left text-sm text-slate-500 font-medium`}
    >
      <div
        className={`inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-700 ${
          alignRight ? "ml-auto" : ""
        }`}
        onClick={() => onChange(sortKey, isActive && currentDir === "asc" ? "desc" : "asc")}
      >
        <span>{label}</span>
        <SortIcon active={isActive} direction={currentDir} />
      </div>
    </th>
  );
}


function WelcomeInviteSection({ user, companyId, onSent }) {
  const [sending, setSending] = React.useState(false);
  const [channel, setChannel] = React.useState("email");

  // Auto-generated placeholder emails (POS imports) are not deliverable
  // inboxes — sending there would record welcome_send_count and flip the row
  // to "Invite sent" without the employee ever receiving anything.
  const emailIsPlaceholder = isAutoGeneratedEmail(user.email);
  const hasEmail = Boolean(user.email) && !emailIsPlaceholder;
  // Gate on phone *validity*, not just presence — mirrors the store-level
  // welcome modal, which learned that legacy records carry malformed phones
  // the backend's SMS normalization would reject.
  const hasPhone = isValidSMSPhone(user.phone_number);

  // The drawer stays mounted while the roster re-points it at other users,
  // so reset to the best available channel per user rather than per mount.
  React.useEffect(() => {
    setChannel(hasEmail ? "email" : "sms");
    setSending(false);
  }, [user.user_id, hasEmail]);

  // Only relevant while the user has never logged in and can still receive
  // an invite; archived-only users have no active membership to send from.
  if (user.last_login_at || !user.any_active_stm) return null;

  const hasInvite = user.welcome_send_count > 0;
  const selectedChannelAvailable =
    (channel === "email" && hasEmail) || (channel === "sms" && hasPhone);
  const noChannelAvailable = !hasEmail && !hasPhone;

  const handleSend = async () => {
    const channelLabel = channel === "sms" ? "SMS" : "email";
    setSending(true);
    try {
      await postRightworkAPI(API_send_welcome_to_company_user, {
        company_id: companyId,
        user_id: user.user_id,
        channel,
      });
      toast.success(`Welcome ${channelLabel} sent.`, { autoClose: 2500 });
      onSent?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          `Failed to send welcome ${channelLabel}.`,
        { autoClose: 4000 },
      );
    } finally {
      setSending(false);
    }
  };

  const channelPill = (value, label, available, disabledHint) => {
    const selected = channel === value;
    const pill = (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={!available}
        onClick={() => setChannel(value)}
        className={`rounded-full border px-3 py-1 text-xs font-medium ${
          !available
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
            : selected
              ? "border-violet-700 bg-violet-50 text-violet-700"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        }`}
      >
        {label}
      </button>
    );
    if (available) return pill;
    return (
      <Tooltip key={value} title={disabledHint} placement="top" arrow>
        <span>{pill}</span>
      </Tooltip>
    );
  };

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50 p-3.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
        Welcome invite
      </h3>
      <p className="mt-1.5 text-sm text-amber-900">
        Never logged in. {formatInviteStatus(user)}.
      </p>
      <div
        className="mt-2.5 flex items-center gap-1.5"
        role="radiogroup"
        aria-label="Welcome invite channel"
      >
        {channelPill(
          "email",
          "Email",
          hasEmail,
          emailIsPlaceholder
            ? "Auto-generated placeholder email, add a real one first"
            : "No email on file",
        )}
        {channelPill(
          "sms",
          "Text",
          hasPhone,
          user.phone_number
            ? "Phone number isn't supported for SMS"
            : "No phone number on file",
        )}
      </div>
      {selectedChannelAvailable && (
        <p className="mt-1.5 text-xs text-amber-700">
          {channel === "sms"
            ? `Will text ${formatPhoneNumber(user.phone_number)}`
            : `Will email ${user.email}`}
        </p>
      )}
      <button
        type="button"
        onClick={handleSend}
        disabled={sending || !selectedChannelAvailable}
        className="mt-2.5 rounded-md bg-violet-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {sending ? "Sending..." : hasInvite ? "Resend invite" : "Send invite"}
      </button>
      {noChannelAvailable && (
        <p className="mt-1.5 text-xs text-rose-600">
          {emailIsPlaceholder
            ? "This profile only has an auto-generated placeholder email. Add a real email or a valid SMS phone number before sending an invite."
            : "Add an email or a valid SMS phone number to this profile before sending an invite."}
        </p>
      )}
    </section>
  );
}


function UserDetailDrawer({ user, onClose, companyId, companyStores, onProfileSaved, onMembershipAction }) {
  if (!user) return null;
  const activeStms = user.stms.filter((s) => !s.is_archived);
  const archivedStms = user.stms.filter((s) => s.is_archived);
  // "Apply to other stores" only makes sense when there's at least one other
  // active membership to apply to. Drives whether those menu items show.
  const applyToOthersAvailable = activeStms.length > 1;
  // "+ Add to store" disables when the user is already active in every store
  // in the company; otherwise the picker would open empty.
  const canAddStore = (companyStores?.length || 0) > activeStms.length;
  return (
    <div className="flex h-full w-[560px] max-w-full flex-col bg-white">
      {/* Sticky header so the user's name stays visible while scrolling
          through long membership lists. */}
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-slate-900">
            {user.full_name || "(no name)"}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <UserProfileSection
          user={user}
          companyId={companyId}
          onSaved={onProfileSaved}
        />

        <WelcomeInviteSection
          user={user}
          companyId={companyId}
          onSent={onProfileSaved}
        />

        {user.company_overrides.length > 0 && (
          <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
              Company-level access
            </h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {user.company_overrides.map((accessKey) => (
                <VirtualOverrideBadge key={accessKey} accessKey={accessKey} />
              ))}
            </div>
          </section>
        )}

        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              Store memberships {activeStms.length > 0 && `(${activeStms.length})`}
            </h3>
            <Tooltip
              title={canAddStore ? "" : "Already a member of every store"}
              placement="top"
              arrow
              disableHoverListener={canAddStore}
            >
              <span>
                <button
                  type="button"
                  onClick={() => onMembershipAction("grant_new_store", { userId: user.user_id })}
                  disabled={!canAddStore}
                  className="text-xs font-medium text-violet-700 hover:text-violet-900 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  + Add to store
                </button>
              </span>
            </Tooltip>
          </div>
          <div className="mt-2 space-y-2">
            {activeStms.length === 0 && (
              <p className="text-sm text-slate-500">No active store memberships.</p>
            )}
            {activeStms.map((stm) => (
              <div key={stm.stm_id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate text-sm font-medium text-slate-900">{stm.store_name}</p>
                  <div className="flex items-center gap-1">
                    <PermissionBadge permissionType={stm.permission_type} />
                    <MembershipActionMenu
                      stm={stm}
                      userId={user.user_id}
                      applyToOthersAvailable={applyToOthersAvailable}
                      onAction={onMembershipAction}
                    />
                  </div>
                </div>
                {stm.permission_type_overrides.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {stm.permission_type_overrides.map((accessKey) => (
                      <StoreOverrideIcon key={accessKey} accessKey={accessKey} />
                    ))}
                  </div>
                )}
                {stm.joined && (
                  <p className="mt-1.5 text-[11px] text-slate-400">
                    Joined {new Date(stm.joined).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {archivedStms.length > 0 && (
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              Archived ({archivedStms.length})
            </h3>
            <div className="mt-2 space-y-2">
              {archivedStms.map((stm) => (
                <div
                  key={stm.stm_id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 opacity-75"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-medium text-slate-700">{stm.store_name}</p>
                    <div className="flex items-center gap-1">
                      <PermissionBadge permissionType={stm.permission_type} />
                      <MembershipActionMenu
                        stm={stm}
                        userId={user.user_id}
                        onAction={onMembershipAction}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function CompanyTeamDisplayConfigurations(props) {
  // Only companies where the user has at least one company_admin or
  // rightwork_internal STM should be selectable — anything less will fail
  // backend authorization (UserIsCompanyAdmin) and trap the tab in an error
  // loop. Membership info (storeOptions) carries permission_type per store;
  // we join it with storeNames (which carries the company_id per store) to
  // derive the set of administrable company_ids.
  const adminCompanyIds = React.useMemo(() => {
    const ADMIN_PERMISSIONS = new Set(["company_admin", "rightwork_internal"]);
    const adminStoreIds = new Set(
      (props.storeOptions || [])
        .filter((m) => ADMIN_PERMISSIONS.has(m?.permission_type))
        .map((m) => m.store),
    );
    const companyIds = new Set();
    (props.storeNames || []).forEach((s) => {
      if (s?.company != null && adminStoreIds.has(s.id)) {
        companyIds.add(s.company);
      }
    });
    return companyIds;
  }, [props.storeOptions, props.storeNames]);

  const companyOptions = React.useMemo(
    () => buildCompanyOptionsFromStoreNames(props.storeNames)
      .filter((c) => adminCompanyIds.has(c.id)),
    [props.storeNames, adminCompanyIds],
  );

  const [selectedCompanyId, setSelectedCompanyId] = React.useState(
    () => (adminCompanyIds.has(props.homeCompany) ? props.homeCompany : null)
      || companyOptions[0]?.id
      || null,
  );
  const [searchInput, setSearchInput] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState([]);
  const [storeFilter, setStoreFilter] = React.useState([]);
  const [overrideFilter, setOverrideFilter] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [sort, setSort] = React.useState("name");
  const [sortDir, setSortDir] = React.useState("asc");
  const [page, setPage] = React.useState(1);

  const [loading, setLoading] = React.useState(true);
  // Tracks assistant-driven background refreshes that intentionally skip
  // the loading spinner. Pagination is disabled while any of them is in
  // flight so a Load More click can't compute page+1 from pre-refresh
  // state and race the page-1 replacement that's about to land.
  //
  // Counter-not-boolean because multiple signals can fire close in time
  // (e.g. an admin runs two chat actions back-to-back). With a single
  // boolean, the FIRST refresh's finally would clear it while the second
  // is still in flight, re-enabling Load More too early. The ref tracks
  // active count; the boolean state mirrors "count > 0" for the disable
  // check, transitioning only when the count crosses zero.
  const silentRefreshCountRef = React.useRef(0);
  const [silentRefreshing, setSilentRefreshing] = React.useState(false);
  // Set when an assistant signal arrives but the user has paginated past
  // page 1. We don't auto-truncate their loaded rows back to a single
  // page — instead we surface a "list may be out of date" banner so they
  // explicitly opt into the reset. Cleared by any non-silent reload
  // (filter/sort/company change, or the banner's own Refresh click).
  const [hasStaleData, setHasStaleData] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [pagination, setPagination] = React.useState({ total: 0, total_pages: 0 });
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [companyStores, setCompanyStores] = React.useState([]);

  // Bulk-action selection — Set of user_id. Per-user (not per-STM) since the
  // actions span all of a user's STMs. Persists across Load More clicks but
  // is cleared on filter change (same lifecycle as users[]) so it can't hold
  // IDs that aren't in the visible result set.
  const [selectedUserIds, setSelectedUserIds] = React.useState(() => new Set());
  const [bulkModal, setBulkModal] = React.useState(null);  // null | "grant" | "archive" | "permission" | "overrides"
  // When the drawer's per-row menu opens a bulk modal, this carries the
  // single-user/single-store prefill so the modal opens pre-scoped instead
  // of defaulting to "current selection × all stores". null when the modal
  // was opened from the toolbar (selection-driven) instead.
  const [drawerActionContext, setDrawerActionContext] = React.useState(null);
  const [addUserOpen, setAddUserOpen] = React.useState(false);

  // Token bumps when the filter/sort/search/company context changes — i.e.,
  // anything that invalidates the existing result set. In-flight responses
  // captured against a stale token are discarded so they don't write into
  // the new result set. Pagination intentionally does NOT bump the token:
  // page 2 and page 3 belong to the same result set, so both responses must
  // be accepted (and contiguously appended) to avoid skipping rows.
  const filterContextRef = React.useRef(0);

  React.useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const storeOptionsForCompany = React.useMemo(() => {
    return companyStores.map((s) => ({
      value: s.id,
      label: s.store_name,
      badge: s.status === "closed" ? "closed" : null,
    }));
  }, [companyStores]);

  const handleCompanyChange = React.useCallback((nextCompanyId) => {
    setSelectedCompanyId(nextCompanyId);
    setStoreFilter([]);
  }, []);

  React.useEffect(() => {
    // Reset to the first administrable company if the current selection isn't
    // (or no longer is) one the user can administer. Covers initial mount with
    // a stale homeCompany as well as mid-session permission/membership changes.
    const isValidSelection = selectedCompanyId != null && adminCompanyIds.has(selectedCompanyId);
    if (!isValidSelection && companyOptions[0]?.id) {
      setSelectedCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, selectedCompanyId, adminCompanyIds]);

  // fetchPage is the single fetch primitive for both flows:
  //   - filter/sort/company changes: fetchPage(1, replace=true)
  //   - Load more click:             fetchPage(page + 1, replace=false)
  //   - ops-assistant signal:        fetchPage(1, replace=true, silent=true)
  // setPage advances ONLY on success — if a load-more fetch fails, page stays
  // put so a retry re-issues the same page rather than skipping ahead and
  // leaving a permanent gap in the result set.
  //
  // ``silent`` skips the loading-state toggles so a chat-driven refresh
  // doesn't flash the spinner over the rows the user is already looking at.
  // The result still replaces users[] when it arrives, so stale data is
  // bounded by the fetch latency, not by another user interaction.
  const fetchPage = React.useCallback(async (targetPage, replace, silent = false) => {
    if (!selectedCompanyId) {
      if (!silent) setLoading(false);
      return;
    }
    const filterContextAtStart = filterContextRef.current;
    if (silent) {
      silentRefreshCountRef.current += 1;
      if (silentRefreshCountRef.current === 1) setSilentRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const params = {
        company_id: selectedCompanyId,
        page: targetPage,
        page_size: PAGE_SIZE,
        status: statusFilter,
        sort,
        sort_dir: sortDir,
      };
      if (searchQuery) params.q = searchQuery;
      if (roleFilter.length) params.role = roleFilter.join(",");
      if (storeFilter.length) params.store_id = storeFilter.join(",");
      if (overrideFilter.length) params.override = overrideFilter.join(",");

      const response = await getRightworkAPI(API_get_company_users, params);
      // Discard only if the filter context has changed since the request was
      // issued; pagination responses (same context) always merge.
      if (filterContextAtStart !== filterContextRef.current) return;
      const payload = response?.data?.redux?.payload;
      const newUsers = payload?.users || [];
      setUsers((prev) => (replace ? newUsers : [...prev, ...newUsers]));
      setPagination(payload?.pagination || { total: 0, total_pages: 0 });
      // Stores list is the same on every page; only refresh on page 1 to avoid
      // pointlessly clobbering it during load-more clicks.
      if (targetPage === 1) setCompanyStores(payload?.stores || []);
      setPage(targetPage);
    } catch (error) {
      if (filterContextAtStart !== filterContextRef.current) return;
      toast.error(
        error?.response?.data?.system?.message || "Unable to load company team.",
        { autoClose: 3000 },
      );
    } finally {
      if (silent) {
        // Decrement unconditionally — even a discarded silent fetch must
        // release its slot, otherwise the count drifts upward and Load
        // More stays permanently disabled. Only flip the boolean state
        // back to false on the LAST in-flight silent fetch (count
        // returning to zero); otherwise an earlier-finishing refresh
        // would re-enable Load More while a later one is still in flight.
        silentRefreshCountRef.current = Math.max(
          0, silentRefreshCountRef.current - 1,
        );
        if (silentRefreshCountRef.current === 0) setSilentRefreshing(false);
      } else if (filterContextAtStart === filterContextRef.current) {
        setLoading(false);
      }
    }
  }, [selectedCompanyId, statusFilter, sort, sortDir, searchQuery, roleFilter, storeFilter, overrideFilter]);

  // Filter/sort/company change: bump the in-flight context (so any pending
  // request gets discarded), reset visible state, then fetch page 1.
  React.useEffect(() => {
    filterContextRef.current += 1;
    setUsers([]);
    setPagination({ total: 0, total_pages: 0 });
    setLoading(true);
    setHasStaleData(false);
    fetchPage(1, true);
  }, [fetchPage]);

  // Selection persists across search, filter, sort, and pagination — those
  // are find/narrow operations, and bulk actions act on user_ids regardless
  // of what's currently visible. Company is the one hard scope boundary:
  // a user_id selected in company A can't be acted on under company B's
  // permission gate, so carrying it across would just be dead state.
  React.useEffect(() => {
    setSelectedUser(null);
    setSelectedUserIds(new Set());
  }, [selectedCompanyId]);

  // Refetch when the ops assistant reports a confirmed team-management
  // mutation. Without this, an admin updating someone's email/permissions
  // via chat would see stale rows here until they manually refreshed.
  // The timestamp ref guards against the effect's natural re-runs on
  // fetchPage changes — only NEW signals (different timestamp) trigger
  // a refetch.
  //
  // The refetch is silent: we keep the existing rows visible and skip the
  // loading spinner, so a chat-driven update lands as a smooth in-place
  // change rather than a jarring empty-state flash. We still bump
  // filterContextRef so any in-flight pagination load is discarded — the
  // signal-driven page-1 response should win.
  //
  // ``setLoading(false)`` is critical here: if a non-silent request (e.g.
  // a Load More click) was in flight when the signal fired, the
  // filterContextRef bump invalidates it — its ``finally`` will skip the
  // ``setLoading(false)`` because ``filterContextAtStart`` no longer
  // matches. Our silent replacement also skips ``setLoading(false)``,
  // so without an explicit clear here ``loading`` would stay stuck true
  // and "Load more" would render permanently disabled until a full
  // page reload.
  const lastOpsSignalTimestampRef = React.useRef(null);
  React.useEffect(() => {
    const ts = props.opsRefreshSignal?.timestamp;
    if (!ts || ts === lastOpsSignalTimestampRef.current) return;
    lastOpsSignalTimestampRef.current = ts;
    if (!props.opsRefreshSignal?.types?.includes("employees")) return;

    // The assistant action may have archived a selected user or otherwise
    // invalidated the selection set; rather than leave stale user_ids
    // that'd silent-fail on the next bulk action, drop the selection.
    setSelectedUserIds(new Set());

    if (page > 1) {
      // User has loaded multiple pages. A silent ``fetchPage(1, true)``
      // would replace ~250 visible rows with the first 50, snapping
      // them back to the top with no warning. Surface a banner instead
      // so they choose when to re-anchor.
      setHasStaleData(true);
      return;
    }

    filterContextRef.current += 1;
    setLoading(false);
    fetchPage(1, true, /* silent */ true);
  }, [props.opsRefreshSignal, fetchPage, page]);

  const handleLoadMore = () => {
    fetchPage(page + 1, false);
  };

  // Banner action: user accepts the truncation cost in exchange for
  // fresh data. Mirrors the filter-change reset path so any in-flight
  // pagination load gets discarded and we render the spinner explicitly
  // (this is a user-initiated reload, not a silent background refresh).
  const handleStaleRefresh = React.useCallback(() => {
    setHasStaleData(false);
    filterContextRef.current += 1;
    setUsers([]);
    setPagination({ total: 0, total_pages: 0 });
    setLoading(true);
    fetchPage(1, true);
  }, [fetchPage]);

  // Drawer's per-membership menu dispatches here. The payload always carries
  // {userId, storeId} for the source row plus the source row's permission +
  // overrides (used by the apply_* actions).
  //
  // Routing:
  //   permission / overrides / archive  → bulk modal pre-scoped to {user × this store}
  //   reactivate                         → grant modal pre-pointed at this store
  //   apply_permission                   → permission modal pre-scoped to {user × ALL OTHER active stores},
  //                                        permission pre-filled from this row
  //   apply_overrides                    → overrides modal in mirror mode pre-scoped to OTHER active stores
  const handleMembershipAction = React.useCallback((action, payload) => {
    // Drawer's "+ Add to store" header button. Open the grant modal scoped
    // to this single user, with their currently-active stores excluded from
    // the picker so we can't double-grant.
    if (action === "grant_new_store") {
      const sourceUser = selectedUser;
      if (!sourceUser) return;
      const activeStms = sourceUser.stms.filter((s) => !s.is_archived);
      const activeStoreIds = activeStms.map((s) => s.store_id);
      // Default the new membership to mirror the user's existing privileges:
      // their highest current permission and the union of overrides across
      // active stores. Admin can adjust before submitting.
      const overrideUnion = new Set();
      activeStms.forEach((s) =>
        (s.permission_type_overrides || []).forEach((k) => overrideUnion.add(k)),
      );
      setDrawerActionContext({
        userId: payload.userId,
        scope: "stores",
        excludeStoreIds: activeStoreIds,
        permissionType: sourceUser.highest_role || "company_employee",
        addOverrides: [...overrideUnion],
      });
      setBulkModal("grant");
      return;
    }

    if (action === "apply_permission" || action === "apply_overrides") {
      // Defense in depth — the menu item is hidden when there's nowhere to
      // apply to, but bail anyway if somehow nothing else is active.
      const sourceUser = selectedUser;
      if (!sourceUser) return;
      const otherActive = sourceUser.stms.filter(
        (s) => !s.is_archived && s.store_id !== payload.storeId,
      );
      if (otherActive.length === 0) return;

      if (action === "apply_permission") {
        // Scope "all" rather than enumerating other stores: applying the
        // source's permission to itself is a no-op, so the net effect is
        // identical and the modal defaults stay clean.
        setDrawerActionContext({
          userId: payload.userId,
          scope: "all",
          permissionType: payload.sourcePermission,
        });
        setBulkModal("permission");
      } else {
        // Mirror semantics: set the target STMs' override set to exactly the
        // source's set. We add the source's overrides and remove every
        // store-level override NOT in the source — `(existing | add) - remove`
        // resolves to `add` for each target STM regardless of its current state.
        // Source row no-ops because its state already matches.
        const sourceSet = new Set(payload.sourceOverrides || []);
        const addList = [...sourceSet];
        const removeList = STORE_OVERRIDE_OPTIONS
          .map((o) => o.value)
          .filter((v) => !sourceSet.has(v));
        setDrawerActionContext({
          userId: payload.userId,
          scope: "all",
          addOverrides: addList,
          removeOverrides: removeList,
          mirrorMode: true,
        });
        setBulkModal("overrides");
      }
      return;
    }

    // Single-store actions: scope to this one store.
    setDrawerActionContext({
      userId: payload.userId,
      scope: "stores",
      storeIds: [payload.storeId],
    });
    if (action === "reactivate") setBulkModal("grant");
    else setBulkModal(action);
  }, [selectedUser]);

  // What the bulk modals receive. When the drawer is dispatching, the
  // prefill context (single-store OR apply-to-all) takes over; otherwise
  // they fall back to the toolbar selection + default scope.
  const modalSelectedUserIds = drawerActionContext
    ? new Set([drawerActionContext.userId])
    : selectedUserIds;
  const modalInitialScope = drawerActionContext?.scope;
  const modalInitialStoreIds = drawerActionContext?.storeIds;
  const modalInitialPermissionType = drawerActionContext?.permissionType;
  const modalInitialAddOverrides = drawerActionContext?.addOverrides;
  const modalInitialRemoveOverrides = drawerActionContext?.removeOverrides;
  const modalMirrorMode = drawerActionContext?.mirrorMode || false;
  const modalExcludeStoreIds = drawerActionContext?.excludeStoreIds;

  // After ANY action (toolbar or drawer), refetch page 1 and re-point the
  // open drawer at the freshly-loaded user. If the user no longer matches
  // the active filters (e.g. archive at last store + status="active"),
  // close the drawer.
  const refreshAfterAction = React.useCallback(async ({ keepDrawerOpenForUserId } = {}) => {
    setDrawerActionContext(null);
    // The ref MUST be set before kicking off fetchPage. fetchPage's setUsers
    // runs inside its async body and triggers the [users] effect synchronously
    // when React commits the update; if we set the ref after `await fetchPage`,
    // the effect already fired (and bailed) by the time we get here.
    if (keepDrawerOpenForUserId !== undefined) {
      pendingDrawerRefreshRef.current = keepDrawerOpenForUserId;
    }
    await fetchPage(1, true);
  }, [fetchPage]);

  const pendingDrawerRefreshRef = React.useRef(null);
  React.useEffect(() => {
    if (pendingDrawerRefreshRef.current == null) return;
    const targetId = pendingDrawerRefreshRef.current;
    const fresh = users.find((u) => u.user_id === targetId);
    if (fresh) {
      setSelectedUser(fresh);
    } else {
      setSelectedUser(null);  // user no longer matches current filters
    }
    pendingDrawerRefreshRef.current = null;
  }, [users]);

  const toggleSelectUser = React.useCallback((userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const visibleSelectedCount = React.useMemo(() => {
    let count = 0;
    for (const u of users) {
      if (selectedUserIds.has(u.user_id)) count += 1;
    }
    return count;
  }, [users, selectedUserIds]);

  const visibleSelectionState =
    users.length === 0
      ? "none"
      : visibleSelectedCount === 0
      ? "none"
      : visibleSelectedCount === users.length
      ? "all"
      : "some";

  const notVisibleSelectedCount = selectedUserIds.size - visibleSelectedCount;

  const toggleAllVisible = React.useCallback(() => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const allVisibleSelected = users.length > 0 && users.every((u) => next.has(u.user_id));
      if (allVisibleSelected) {
        users.forEach((u) => next.delete(u.user_id));
      } else {
        users.forEach((u) => next.add(u.user_id));
      }
      return next;
    });
  }, [users]);

  const handleSortChange = (sortKey, dir) => {
    setSort(sortKey);
    setSortDir(dir);
  };

  if (companyOptions.length === 0) {
    return (
      <div>
        <div className="py-5 px-6 border-b border-slate-200">
          <h2 className="text-2xl text-slate-900">Company Members</h2>
        </div>
        <div className="px-6 py-16 text-center text-sm text-slate-500">
          You don't have admin access to any companies.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="py-5 px-6 border-b border-slate-200 flex items-center justify-between gap-4">
        <h2 className="text-2xl text-slate-900">Company Members</h2>
        {companyOptions.length > 1 && (
          <div className="w-[260px] shrink-0">
            <CompanyNameSidebar
              companyOptions={companyOptions}
              selectedCompanyId={selectedCompanyId}
              setSelectedCompanyId={handleCompanyChange}
            />
          </div>
        )}
      </div>

      <div className="mt-5 px-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-slate-500 text-sm">Search by name, email, or phone</p>
          <div className="w-[250px] relative mt-1">
            <SearchIcon
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              style={{ fontSize: "18px" }}
            />
            <input
              className="w-full border border-slate-200 pl-9 pr-3 h-[40px] text-sm rounded-lg outline-none transition-all duration-200 hover:border-slate-300 focus:border focus:border-violet-700 focus:shadow-sm"
              type="text"
              placeholder="Search team"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAddUserOpen(true)}
          disabled={!selectedCompanyId}
          className="inline-flex h-[40px] items-center gap-1.5 rounded-lg bg-violet-700 px-4 text-sm font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-violet-300"
        >
          + Add user
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 px-6">
        <MultiSelectFilter
          label="Permission"
          value={roleFilter}
          options={PERMISSION_TYPE_OPTIONS}
          onChange={setRoleFilter}
          icon={VerifiedUserOutlinedIcon}
        />
        <MultiSelectFilter
          label="Store"
          value={storeFilter}
          options={storeOptionsForCompany}
          onChange={setStoreFilter}
          icon={StoreOutlinedIcon}
        />
        <MultiSelectFilter
          label="Special access"
          value={overrideFilter}
          options={OVERRIDE_FILTER_OPTIONS}
          onChange={setOverrideFilter}
          icon={PeopleAltOutlinedIcon}
        />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
        {pagination.total > 0 && (
          <span className="text-xs text-slate-500">
            {pagination.total} {pagination.total === 1 ? "user" : "users"}
          </span>
        )}
      </div>

      {selectedUserIds.size > 0 && (
        <div className="sticky top-2 z-20 mt-3 mx-6 flex flex-wrap items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 rounded-lg shadow-sm">
          <span className="text-sm font-medium text-violet-800">
            {selectedUserIds.size} selected
            {notVisibleSelectedCount > 0 && (
              <span className="ml-1 font-normal text-xs text-violet-600">
                ({notVisibleSelectedCount} not visible)
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setSelectedUserIds(new Set())}
            className="text-xs text-violet-700 hover:text-violet-900 underline"
          >
            Clear
          </button>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setBulkModal("grant")}
              className="px-3 py-1.5 rounded-lg border border-violet-300 bg-white text-sm text-violet-800 hover:bg-violet-50"
            >
              Add to store
            </button>
            <button
              type="button"
              onClick={() => setBulkModal("permission")}
              className="px-3 py-1.5 rounded-lg border border-violet-300 bg-white text-sm text-violet-800 hover:bg-violet-50"
            >
              Change permission
            </button>
            <button
              type="button"
              onClick={() => setBulkModal("overrides")}
              className="px-3 py-1.5 rounded-lg border border-violet-300 bg-white text-sm text-violet-800 hover:bg-violet-50"
            >
              Special access
            </button>
            <button
              type="button"
              onClick={() => setBulkModal("archive")}
              className="px-3 py-1.5 rounded-lg border border-rose-300 bg-white text-sm text-rose-700 hover:bg-rose-50"
            >
              Archive
            </button>
          </div>
        </div>
      )}

      {hasStaleData && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5">
          <p className="text-sm text-violet-900">
            This list may be out of date — the assistant changed team data
            while you were paginated past page 1.
          </p>
          <button
            type="button"
            onClick={handleStaleRefresh}
            className="shrink-0 rounded-md bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800"
          >
            Refresh
          </button>
        </div>
      )}

      <div className="mt-4">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="w-[3%] pl-6 py-3.5 text-left"
                    >
                      <Checkbox
                        size="small"
                        checked={visibleSelectionState === "all"}
                        indeterminate={visibleSelectionState === "some"}
                        onChange={toggleAllVisible}
                        sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, "&.MuiCheckbox-indeterminate": { color: "#6d28d9" }, p: 0 }}
                        inputProps={{ "aria-label": "Select all visible users" }}
                      />
                    </th>
                    <SortableTh
                      label="Name"
                      sortKey="name"
                      currentSort={sort}
                      currentDir={sortDir}
                      onChange={handleSortChange}
                      widthClass="w-[16%]"
                    />
                    <th
                      scope="col"
                      className="w-[21%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Contact
                    </th>
                    <SortableTh
                      label="Stores"
                      sortKey="store_count"
                      currentSort={sort}
                      currentDir={sortDir}
                      onChange={handleSortChange}
                      widthClass="w-[8%]"
                    />
                    <SortableTh
                      label="Highest permission"
                      sortKey="highest_role"
                      currentSort={sort}
                      currentDir={sortDir}
                      onChange={handleSortChange}
                      widthClass="w-[14%]"
                    />
                    <th
                      scope="col"
                      className="w-[28%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Special access
                    </th>
                    <SortableTh
                      label="Last login"
                      sortKey="last_login"
                      currentSort={sort}
                      currentDir={sortDir}
                      onChange={handleSortChange}
                      widthClass="w-[10%] pr-6"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {loading && users.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="flex min-h-[300px] items-center justify-center">
                          <LoadSpinner />
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-sm text-slate-500">
                        No users match these filters.
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <CompanyUserRow
                        key={user.user_id}
                        user={user}
                        onClick={() => setSelectedUser(user)}
                        isSelected={selectedUserIds.has(user.user_id)}
                        onToggleSelect={() => toggleSelectUser(user.user_id)}
                      />
                    ))
                  )}
                </tbody>
              </table>
              <div className="border-b border-slate-200" />
            </div>
          </div>
        </div>
      </div>

      {pagination.total > 0 && (
        <div className="mt-4 flex flex-col items-center gap-2 px-6 pb-6">
          {users.length < pagination.total && (
            <button
              type="button"
              disabled={loading || silentRefreshing}
              onClick={handleLoadMore}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <><span className="sr-only">Loading more team members</span><span aria-hidden="true" className="flex justify-center"><LoadSpinner /></span></> : `Load more (${pagination.total - users.length} remaining)`}
            </button>
          )}
          <p className="text-xs text-slate-500">
            Showing {users.length} of {pagination.total}
          </p>
        </div>
      )}

      <Drawer
        anchor="right"
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
      >
        <UserDetailDrawer
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          companyId={selectedCompanyId}
          companyStores={companyStores}
          onProfileSaved={() => {
            // Refresh + keep drawer open with the new identity values.
            refreshAfterAction({ keepDrawerOpenForUserId: selectedUser?.user_id });
          }}
          onMembershipAction={handleMembershipAction}
        />
      </Drawer>

      <BulkGrantStoreAccessModal
        open={bulkModal === "grant"}
        onClose={() => {
          setBulkModal(null);
          setDrawerActionContext(null);
        }}
        selectedUserIds={modalSelectedUserIds}
        initialStoreIds={modalInitialStoreIds}
        excludeStoreIds={modalExcludeStoreIds}
        initialPermissionType={modalInitialPermissionType}
        initialOverrides={modalInitialAddOverrides}
        companyId={selectedCompanyId}
        companyStores={companyStores}
        onSuccess={() => {
          // Toolbar grants clear selection (the action is "done" for those
          // users). Drawer-dispatched grants (reactivate) leave selection
          // alone. Either way: refresh + re-point any open drawer.
          if (!drawerActionContext) setSelectedUserIds(new Set());
          refreshAfterAction({
            keepDrawerOpenForUserId: drawerActionContext?.userId ?? selectedUser?.user_id,
          });
        }}
      />

      <BulkArchiveModal
        open={bulkModal === "archive"}
        onClose={() => {
          setBulkModal(null);
          setDrawerActionContext(null);
        }}
        selectedUserIds={modalSelectedUserIds}
        initialScope={modalInitialScope}
        initialStoreIds={modalInitialStoreIds}
        companyId={selectedCompanyId}
        companyStores={companyStores}
        onSuccess={(payload) => {
          if (!drawerActionContext) {
            setSelectedUserIds(new Set());
          } else {
            // Drawer-archive path: prune the acted-on user AND any users
            // whose final STM was archived by this batch (cascade-deactivated).
            // Their rows vanish from the roster on refetch, so leaving them
            // selected would inflate the toolbar count with dead IDs.
            const drop = new Set(payload?.deactivated_user_ids || []);
            if (drawerActionContext?.userId != null) drop.add(drawerActionContext.userId);
            if (drop.size > 0) {
              setSelectedUserIds((prev) => {
                const next = new Set(prev);
                drop.forEach((id) => next.delete(id));
                return next;
              });
            }
          }
          refreshAfterAction({
            keepDrawerOpenForUserId: drawerActionContext?.userId ?? selectedUser?.user_id,
          });
        }}
      />

      <BulkChangePermissionModal
        open={bulkModal === "permission"}
        onClose={() => {
          setBulkModal(null);
          setDrawerActionContext(null);
        }}
        selectedUserIds={modalSelectedUserIds}
        initialScope={modalInitialScope}
        initialStoreIds={modalInitialStoreIds}
        initialPermissionType={modalInitialPermissionType}
        companyId={selectedCompanyId}
        companyStores={companyStores}
        onSuccess={() => {
          // Don't clear selection — admins commonly chain permission +
          // overrides changes on the same user set.
          refreshAfterAction({
            keepDrawerOpenForUserId: drawerActionContext?.userId ?? selectedUser?.user_id,
          });
        }}
      />

      <BulkSpecialAccessModal
        open={bulkModal === "overrides"}
        onClose={() => {
          setBulkModal(null);
          setDrawerActionContext(null);
        }}
        selectedUserIds={modalSelectedUserIds}
        initialScope={modalInitialScope}
        initialStoreIds={modalInitialStoreIds}
        initialAddOverrides={modalInitialAddOverrides}
        initialRemoveOverrides={modalInitialRemoveOverrides}
        mirrorMode={modalMirrorMode}
        companyId={selectedCompanyId}
        companyStores={companyStores}
        onSuccess={() => {
          refreshAfterAction({
            keepDrawerOpenForUserId: drawerActionContext?.userId ?? selectedUser?.user_id,
          });
        }}
      />

      <AddUserModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        companyId={selectedCompanyId}
        companyStores={companyStores}
        onSuccess={(payload) => {
          // Refresh + open the just-added user's drawer so the admin can
          // verify and continue managing without a manual lookup.
          refreshAfterAction({ keepDrawerOpenForUserId: payload?.user_id });
        }}
      />
    </div>
  );
}


function CompanyUserRow({ user, onClick, isSelected, onToggleSelect }) {
  const { overrideStoreCounts, highestRoleCount } = React.useMemo(() => {
    // Match the backend roll-up scope: prefer active STMs, fall back to
    // archived for users whose STMs are all archived. Otherwise fractions
    // collapse to 0/N for archived-only rows even though the row aggregates
    // are computed from those archived STMs.
    const activeStms = user.stms.filter((stm) => !stm.is_archived);
    const rollUpStms = activeStms.length > 0 ? activeStms : user.stms;

    const overrides = {};
    let highestCount = 0;
    rollUpStms.forEach((stm) => {
      (stm.permission_type_overrides || []).forEach((accessKey) => {
        overrides[accessKey] = (overrides[accessKey] || 0) + 1;
      });
      if (stm.permission_type === user.highest_role) {
        highestCount += 1;
      }
    });
    return { overrideStoreCounts: overrides, highestRoleCount: highestCount };
  }, [user.stms, user.highest_role]);

  const total = user.store_count;
  const storeWord = total === 1 ? "store" : "stores";

  const buildOverrideTooltip = (accessKey) => {
    const label = SPECIAL_ACCESS_LABELS[accessKey] || accessKey;
    const count = overrideStoreCounts[accessKey] || 0;
    if (!total) return label;
    return `${label} — ${count}/${total} ${storeWord}`;
  };

  const highestRoleTooltip = total
    ? `${PERMISSION_TYPE_LABEL[user.highest_role] || user.highest_role} — ${highestRoleCount}/${total} ${storeWord}`
    : null;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <tr
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${user.full_name || "user"} details`}
      className={`hover:bg-slate-50 cursor-pointer focus:outline-none focus:bg-slate-50 focus:ring-2 focus:ring-inset focus:ring-violet-500 ${
        isSelected ? "bg-violet-50" : "bg-white"
      }`}
    >
      <td className="whitespace-nowrap pl-6 py-2" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={onToggleSelect}
          sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, p: 0 }}
          inputProps={{ "aria-label": `Select ${user.full_name || "user"}` }}
        />
      </td>
      <td className="whitespace-nowrap py-2 px-3 text-sm text-slate-500">
        <div className="font-medium text-slate-900">
          {user.full_name ? formatName(user.full_name) : "(no name)"}
        </div>
      </td>
      <td className="px-3 py-2 text-sm text-slate-500">
        <div className="flex flex-col space-y-1">
          <div className="flex items-center space-x-2 text-slate-900">
            <EmailIcon style={{ fontSize: "16px" }} className="text-slate-400" />
            <span>{user.email || "No email"}</span>
          </div>
          {user.phone_number && (
            <div className="flex items-center space-x-2 text-slate-600 text-xs">
              <PhoneIcon style={{ fontSize: "14px" }} className="text-slate-400" />
              <span>{formatPhoneNumber(user.phone_number)}</span>
            </div>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-700">
        {user.store_count}
        {!user.any_active_stm && (
          <span className="ml-1 text-[10px] uppercase text-amber-600">archived</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500">
        <PermissionBadge
          permissionType={user.highest_role}
          tooltipTitle={highestRoleTooltip}
          count={highestRoleCount}
          total={total}
        />
      </td>
      <td className="px-3 py-2 text-sm text-slate-500">
        <div className="flex items-center flex-wrap gap-1">
          {user.all_overrides.length === 0 && user.company_overrides.length === 0 && (
            <span className="text-xs text-slate-400">—</span>
          )}
          {user.all_overrides.map((accessKey) => (
            <StoreOverrideIcon
              key={accessKey}
              accessKey={accessKey}
              tooltipTitle={buildOverrideTooltip(accessKey)}
              count={overrideStoreCounts[accessKey] || 0}
              total={total}
            />
          ))}
          {user.company_overrides.map((accessKey) => (
            <VirtualOverrideBadge key={accessKey} accessKey={accessKey} />
          ))}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 pr-6 text-sm text-slate-500">
        {formatLastLogin(user.last_login_at)}
        {!user.last_login_at && user.any_active_stm && (
          <div
            className={`text-[11px] ${
              user.welcome_send_count > 0 ? "text-slate-400" : "text-amber-600"
            }`}
          >
            {formatInviteStatus(user)}
          </div>
        )}
      </td>
    </tr>
  );
}
